import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, Select, Spin, message, Space } from 'antd';
import { FormOutlined, BookOutlined } from '@ant-design/icons';
import { io } from 'socket.io-client';
import ReactEChartsCore from 'echarts-for-react';
import { alarms } from '../../../services/api';
import { isViewer, getStoredUser } from '../../../utils/auth';
import AlarmKnowledge from './AlarmKnowledge';

const LEVEL_MAP = {
  '提示': 'info', '信息': 'info', info: 'info',
  '警告': 'warning', warning: 'warning',
  '严重': 'critical', critical: 'critical',
  '紧急': 'emergency', emergency: 'emergency',
};

function normalizeLevel(level) {
  return LEVEL_MAP[level] || 'info';
}

function formatTime(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('zh-CN', { hour12: false });
}

export default function Alarms() {
  // viewer 只读：隐藏确认/生成工单等写操作按钮（后端 403 兜底）
  const readOnly = isViewer();
  const isAdmin = getStoredUser().role === 'admin';
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState([]);
  const [kbOpen, setKbOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filter === 'urgent') params.status = '未处理';
      if (filter === 'processed') params.status = '已确认';
      // 收敛视图：主告警 + 重复次数 + 最近发生时间
      if (filter === 'grouped') params.view = 'grouped';
      const [listRes, statsRes, trendRes] = await Promise.all([
        alarms.list(params),
        alarms.stats(),
        alarms.trend({ days: 7 }),
      ]);
      setList(listRes.data || []);
      setStats(statsRes.data);
      setTrend(trendRes.data?.items || []);
    } catch (err) {
      message.error(err.message || '加载告警数据失败');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  // 用 ref 持有最新 loadData，避免 WS 连接随筛选条件变化而断开重连
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;
  const wsErrorNotifiedRef = useRef(false);

  useEffect(() => {
    const base = process.env.REACT_APP_API_BASE || window.location.origin;
    const socket = io(base.replace(/\/$/, ''), {
      transports: ['websocket', 'polling'],
      path: '/socket.io/',
      auth: { token: localStorage.getItem('qhy_token') || '' },
    });
    socket.on('connect_error', (err) => {
      // 鉴权失败（token 过期）：清理登录态并跳回登录页，终止重连风暴
      if (String(err?.message || '').toLowerCase().includes('unauthorized')) {
        localStorage.removeItem('qhy_token');
        localStorage.removeItem('qhy_user');
        socket.disconnect();
        window.location.hash = '#/login';
        return;
      }
      // 其他连接异常只提示一次，避免无限重连反复弹窗
      if (!wsErrorNotifiedRef.current) {
        wsErrorNotifiedRef.current = true;
        message.error('实时连接未授权或已断开，告警实时刷新不可用');
      }
    });
    socket.emit('subscribe', 'alarms');
    socket.on('new_alarm', () => {
      message.warning('收到新告警，正在刷新列表');
      loadDataRef.current();
    });
    return () => {
      socket.emit('unsubscribe', 'alarms');
      socket.disconnect();
    };
  }, []);

  const handleAcknowledge = async (id, withWorkOrder = false) => {
    try {
      const res = await alarms.acknowledge(id, { create_work_order: withWorkOrder });
      if (withWorkOrder && res.work_order) {
        message.success(`告警已确认，工单 ${res.work_order.order_no} 已生成`);
      } else {
        message.success(res.message || '告警已确认');
      }
      loadData();
    } catch (err) {
      message.error(err.message || '确认失败');
    }
  };

  const handleCreateWorkOrder = async (id) => {
    try {
      const res = await alarms.createWorkOrder(id);
      message.success(`工单 ${res.data.order_no} 已生成`);
      loadData();
    } catch (err) {
      message.error(err.message || err.response?.data?.message || '生成工单失败');
    }
  };

  const tableData = list.map(item => ({
    key: item.id,
    id: item.id,
    level: normalizeLevel(item.level),
    name: item.title || item.content,
    source: item.parameter || '系统',
    // 收敛视图下时间为最近一次发生时间（含被收敛的重复告警）
    time: formatTime(item.last_occurred_at || item.occurred_at || item.created_at),
    duration: '-',
    status: item.status,
    suppressedCount: item.suppressed_count || 0,
    content: item.content,
    knowledge: item.knowledge || null,
  }));

  const columns = [
    { title: '级别', dataIndex: 'level', render: l => {
      const colors = { info: 'blue', warning: 'orange', critical: 'red', emergency: 'purple' };
      return <Tag color={colors[l] || 'blue'}>{l.toUpperCase()}</Tag>;
    }},
    { title: '告警内容', dataIndex: 'name' },
    { title: '来源', dataIndex: 'source' },
    { title: '时间', dataIndex: 'time' },
    ...(filter === 'grouped' ? [{ title: '重复次数', dataIndex: 'suppressedCount', width: 90, render: v => (
      v > 0 ? <Tag color="orange">+{v}</Tag> : '0'
    )}] : []),
    { title: '持续时间', dataIndex: 'duration' },
    { title: '状态', dataIndex: 'status', render: v => {
      const colors = { '未处理': 'red', '处理中': 'orange', '已确认': 'blue', '已处理': 'green' };
      return <Tag color={colors[v] || 'default'}>{v}</Tag>;
    }},
    { title: '操作', width: 200, render: (_, record) => (
      readOnly ? '-' :
      record.status === '未处理' || record.status === '处理中' ? (
        <Space size="small">
          <Button size="small" onClick={() => handleAcknowledge(record.id, false)}>确认</Button>
          <Button size="small" type="primary" icon={<FormOutlined />} onClick={() => handleAcknowledge(record.id, true)}>
            确认并生成工单
          </Button>
        </Space>
      ) : record.status === '已确认' ? (
        <Button size="small" icon={<FormOutlined />} onClick={() => handleCreateWorkOrder(record.id)}>生成工单</Button>
      ) : '-'
    )},
  ];

  const chartOption = stats ? {
    tooltip: { trigger: 'item' },
    series: [{
      type: 'pie', radius: ['40%', '70%'],
      data: [
        { name: '信息(Info)', value: stats.total.info, itemStyle: { color: '#1890ff' } },
        { name: '警告(Warning)', value: stats.total.warning, itemStyle: { color: '#faad14' } },
        { name: '严重(Critical)', value: stats.total.critical, itemStyle: { color: '#ff4d4f' } },
        { name: '紧急(Emergency)', value: stats.total.emergency, itemStyle: { color: '#eb2f96' } },
      ],
      label: { formatter: '{b}\n{d}%' }
    }]
  } : {};

  const todayTotal = stats ? Object.values(stats.today).reduce((a, b) => a + b, 0) : 0;
  const unhandled = list.filter(a => a.status === '未处理').length;
  const processing = list.filter(a => a.status === '处理中').length;
  const handled = list.filter(a => a.status === '已确认' || a.status === '已处理').length;

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="今日告警" value={todayTotal} suffix="条" valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="未处理" value={unhandled} suffix="条" valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="处理中" value={processing} suffix="条" valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="已处理" value={handled} suffix="条" valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={8}><Card title="告警分布" size="small"><ReactEChartsCore option={chartOption} style={{ height: 250 }} /></Card></Col>
        <Col span={16}><Card title="告警趋势 (7天)" size="small"><ReactEChartsCore option={{
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: trend.map(d => d.label) },
          yAxis: { type: 'value', minInterval: 1 },
          series: [{ type: 'bar', data: trend.map(d => d.count), itemStyle: { color: '#faad14' } }]
        }} style={{ height: 250 }} /></Card></Col>
      </Row>
      <Card title="告警列表" style={{ marginTop: 16 }} size="small" extra={
        <Space>
          {isAdmin && (
            <Button size="small" icon={<BookOutlined />} onClick={() => setKbOpen(true)}>知识库管理</Button>
          )}
          <Select value={filter} onChange={setFilter} style={{ width: 120 }}>
            <Select.Option value="all">全部</Select.Option>
            <Select.Option value="urgent">未处理</Select.Option>
            <Select.Option value="processed">已确认</Select.Option>
            <Select.Option value="grouped">收敛视图</Select.Option>
          </Select>
        </Space>
      }>
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          size="small"
          expandable={{
            // 展开行：告警详情 + 知识库匹配到的故障原因与修复建议
            expandedRowRender: record => (
              <div style={{ margin: 0 }}>
                <p style={{ margin: '4px 0' }}><b>告警详情：</b>{record.content || record.name}</p>
                {record.knowledge ? (
                  <>
                    <p style={{ margin: '4px 0' }}>
                      <b>故障原因</b>
                      {record.knowledge.level_suggestion && <Tag style={{ marginLeft: 8 }}>建议级别: {record.knowledge.level_suggestion}</Tag>}
                      ：{record.knowledge.cause || '暂无'}
                    </p>
                    <p style={{ margin: '4px 0' }}><b>修复建议：</b>{record.knowledge.suggestion || '暂无'}</p>
                  </>
                ) : (
                  <p style={{ margin: '4px 0', color: '#999' }}>知识库暂无该类型告警的处置建议</p>
                )}
              </div>
            ),
          }}
        />
      </Card>
      <AlarmKnowledge open={kbOpen} onClose={() => setKbOpen(false)} />
    </div>
  );
}
